import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { GoogleIcon, RestaurantIcon, DeliveryIcon } from '../components/icons';
import { useData } from '../context/DataContext';


interface ProAuthScreenProps {
    onBack: () => void;
    onForgotPassword: () => void;
    onSwitchToClient: () => void;
}

const ProAuthScreen: React.FC<ProAuthScreenProps> = ({ onBack, onForgotPassword, onSwitchToClient }) => {
    const [activeRole, setActiveRole] = useState<'restaurateur' | 'livreur'>('restaurateur');
    const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
    const [isSuperAdminPortal, setIsSuperAdminPortal] = useState(false);

    useEffect(() => {
        const isAdmin = localStorage.getItem('yumii_admin_mode') === 'true';
        if (isAdmin) {
            setIsSuperAdminPortal(true);
            setActiveTab('login');
        }
    }, []);

    const handleAdminBack = () => {
        localStorage.removeItem('yumii_admin_mode');
        onBack();
    };

    // Shared Form State
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState(''); // Only for register
    const [restaurantName, setRestaurantName] = useState(''); // Only for restaurant register
    const [phone, setPhone] = useState(''); // For restaurant and livreur register
    const [wilaya, setWilaya] = useState(''); // For restaurant and livreur register

    // UI State
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [showRoleMismatchModal, setShowRoleMismatchModal] = useState(false);
    const [showIncorrectPasswordPopup, setShowIncorrectPasswordPopup] = useState(false);
    const [showExistingUserPopup, setShowExistingUserPopup] = useState(false);
    const [cooldown, setCooldown] = useState(0);

    const { wilayas } = useData();
    const { login, register, loginWithGoogle, pendingRoleMismatch, clearRoleMismatch } = useAuth();
    const { setView } = useContext(AppContext);

    useEffect(() => {
        let timer: number;
        if (cooldown > 0) {
            timer = window.setInterval(() => setCooldown(prev => prev - 1), 1000);
        }
        return () => clearInterval(timer);
    }, [cooldown]);

    useEffect(() => {
        if (pendingRoleMismatch) {
            setError(pendingRoleMismatch.message + " Souhaitez-vous rejoindre l'espace Client ?");
            setShowRoleMismatchModal(true);
            clearRoleMismatch();
        }
    }, [pendingRoleMismatch, clearRoleMismatch]);

    const handleTabSwitch = (tab: 'login' | 'register') => {
        setActiveTab(tab);
        setError('');
        setShowErrorModal(false);
    };

    const handleRoleSwitch = (role: 'restaurateur' | 'livreur') => {
        setActiveRole(role);
        setError('');
    };

    // --- Handlers ---
    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        const cleanEmail = email.replace(/\s/g, '').trim();
        if (!cleanEmail || !password) {
            setError("Veuillez remplir tous les champs.");
            setShowErrorModal(true);
            setIsLoading(false);
            return;
        }

        try {
            await login(cleanEmail, password, isSuperAdminPortal ? 'admin' : activeRole);
            localStorage.removeItem('yumii_admin_mode'); // Clear flag on success
            setView({ screen: 'home' }); // Will be redirected by MainApp based on role
        } catch (err: any) {
            const errorMsg = (err?.message || String(err)).toLowerCase();
            if (errorMsg.includes('abort') || errorMsg.includes('cancelled')) return;

            if (errorMsg.includes('not_registered')) {
                setError("Ce compte n'est pas encore inscrit. Veuillez passer à l'inscription.");
                setShowErrorModal(true);
            } else if (errorMsg.includes(`wrong_role_login`)) {
                if (errorMsg.includes(':client')) {
                    setError("Ces identifiants appartiennent à un profil Client. Souhaitez-vous rejoindre l'espace Client ?");
                    setShowRoleMismatchModal(true);
                } else if (errorMsg.includes(':restaurateur')) {
                    setError("Cette adresse email appartient à un compte Restaurant. Veuillez sélectionner le rôle Restaurant ou vous connecter.");
                    setShowErrorModal(true);
                } else if (errorMsg.includes(':livreur')) {
                    setError("Cette adresse email appartient à un compte Livreur. Veuillez sélectionner le rôle Livreur ou vous connecter.");
                    setShowErrorModal(true);
                } else {
                    setError("Le rôle de ce compte ne correspond pas au rôle sélectionné.");
                    setShowErrorModal(true);
                }
            } else {
                let displayMsg = "Identifiants incorrects ou erreur de connexion.";
                if (errorMsg.includes('email not confirmed')) {
                    displayMsg = "Veuillez confirmer votre email avant de vous connecter.";
                    setError(displayMsg);
                    setShowErrorModal(true);
                } else if (
                    errorMsg.includes('invalid login credentials') ||
                    errorMsg.includes('invalid password') ||
                    errorMsg.includes('login failed') ||
                    errorMsg.includes('invalid')
                ) {
                    setShowIncorrectPasswordPopup(true);
                } else {
                    setError(displayMsg + " (" + errorMsg + ")");
                    setShowErrorModal(true);
                }
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        if (cooldown > 0) return;

        if (password.length < 6) {
            setError("Le mot de passe doit contenir au moins 6 caractères.");
            setShowErrorModal(true);
            return;
        }

        const cleanEmail = email.replace(/\s/g, '').trim();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(cleanEmail)) {
            setError("L'adresse email est invalide.");
            setShowErrorModal(true);
            return;
        }

        if (activeRole === 'restaurateur' && (!restaurantName || !phone || !wilaya)) {
            setError("Veuillez remplir tous les champs (Nom du Restaurant, Téléphone et Wilaya sont requis).");
            setShowErrorModal(true);
            return;
        }

        if (activeRole === 'livreur' && (!phone || !wilaya)) {
            setError("Veuillez remplir tous les champs (Téléphone et Wilaya sont requis pour les livreurs).");
            setShowErrorModal(true);
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const result = await register(name, cleanEmail, password, activeRole, restaurantName, phone, wilaya);

            if (result && !result.success) {
                throw new Error((result as any).error || "user already exists");
            }

            if (result && result.success && result.message) {
                setSuccessMessage(result.message);
            }
        } catch (err: any) {
            const errorMsg = (err?.message || String(err)).toLowerCase();
            if (errorMsg.includes('abort') || errorMsg.includes('cancelled')) {
                setIsLoading(false);
                return;
            }

            if (errorMsg.includes('wrong_role_register')) {
                if (errorMsg.includes(':client')) {
                    setError("Cette adresse email est déjà utilisée pour un profil Client. Souhaitez-vous rejoindre l'espace Client ?");
                    setShowRoleMismatchModal(true);
                } else if (errorMsg.includes(':restaurateur')) {
                    setError("Cette adresse email appartient à un compte Restaurant. Veuillez sélectionner le rôle Restaurant ou vous connecter.");
                    setShowErrorModal(true);
                } else if (errorMsg.includes(':livreur')) {
                    setError("Cette adresse email appartient à un compte Livreur. Veuillez sélectionner le rôle Livreur ou vous connecter.");
                    setShowErrorModal(true);
                } else {
                    setError("Cette adresse email appartient à un autre type de compte.");
                    setShowErrorModal(true);
                }
            } else if (errorMsg.includes("invalid") || errorMsg.includes("validation")) {
                setError(`Adresse email "${cleanEmail}" invalide.`);
                setShowErrorModal(true);
            } else if (errorMsg.includes("rate limit")) {
                setError("Trop de tentatives. Veuillez patienter avant de réessayer.");
                setShowErrorModal(true);
                setCooldown(60);
            } else if (errorMsg.includes("already registered") || errorMsg.includes("user already exists") || errorMsg.includes("déjà")) {
                setShowExistingUserPopup(true);
            } else {
                setError(err.message || "Erreur d'inscription. Veuillez vérifier vos informations.");
                setShowErrorModal(true);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleAuth = async () => {
        setIsLoading(true);
        setError('');
        try {
            await loginWithGoogle(activeRole, activeTab); // Pass the selected pro role and intent
            setView({ screen: 'home' });
        } catch (err: any) {
            console.error("Google Auth Error:", err);
            const errorMsg = String(err.message || "").toLowerCase();
            
            if (errorMsg.includes('not_registered')) {
                setError("Ce compte n'est pas encore inscrit. Veuillez d'abord vous inscrire.");
                setShowErrorModal(true);
            } else if (errorMsg.includes('wrong_role_login') || errorMsg.includes('wrong_role_register')) {
                if (errorMsg.includes(':client')) {
                    setError("Ce compte Google appartient à un profil Client. Souhaitez-vous rejoindre l'espace Client ?");
                    setShowRoleMismatchModal(true);
                } else if (errorMsg.includes(':restaurateur')) {
                    setError("Ce compte Google appartient à un compte Restaurant. Veuillez sélectionner le rôle Restaurant ou vous connecter.");
                    setShowErrorModal(true);
                } else if (errorMsg.includes(':livreur')) {
                    setError("Ce compte Google appartient à un compte Livreur. Veuillez sélectionner le rôle Livreur ou vous connecter.");
                    setShowErrorModal(true);
                } else {
                    setError("Le rôle de ce compte ne correspond pas au rôle sélectionné.");
                    setShowErrorModal(true);
                }
            } else {
                setError("Impossible de se connecter avec Google.");
                setShowErrorModal(true);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="h-[100dvh] bg-gray-50 relative flex flex-col overflow-y-auto overflow-x-hidden pt-safe-top pb-safe-bottom">
            {/* Top Header */}
            <div className="pt-8 px-6 pb-2">
                <button 
                  onClick={isSuperAdminPortal ? handleAdminBack : onBack} 
                  className="w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center text-gray-800 hover:bg-gray-100 transition-colors"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
            </div>

            <div className="flex flex-col px-6">
                <div className="text-center mb-6">
                    <div className="h-14 flex justify-center mb-4 mt-2">
                        <img src="/logo-red.svg" alt="Yumii" className="h-full w-auto object-contain" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-1">
                        {isSuperAdminPortal ? 'Portail Super Admin' : 'Espace Professionnel'}
                    </h1>
                    <p className="text-gray-500 text-sm">
                        {isSuperAdminPortal ? 'Accès sécurisé plateforme' : 'Gérez votre activité avec Yumii'}
                    </p>
                </div>

                {/* Role Switcher - Hidden in Admin Mode */}
                {!isSuperAdminPortal && (
                    <div className="bg-gray-200 p-1 rounded-2xl flex mb-6">
                        <button
                            onClick={() => handleRoleSwitch('restaurateur')}
                            className={`flex-1 py-3 text-sm font-bold rounded-xl flex items-center justify-center gap-2 transition-all ${activeRole === 'restaurateur' ? 'bg-red-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                        >
                            <RestaurantIcon className="w-5 h-5" />
                            Restaurant
                        </button>
                        <button
                            onClick={() => handleRoleSwitch('livreur')}
                            className={`flex-1 py-3 text-sm font-bold rounded-xl flex items-center justify-center gap-2 transition-all ${activeRole === 'livreur' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                        >
                            <DeliveryIcon className="w-5 h-5" />
                            Livreur
                        </button>
                    </div>
                )}

                {/* Custom Tabs - Hidden in Admin Mode */}
                {!isSuperAdminPortal && (
                    <div className="bg-gray-200 p-1 rounded-2xl flex mb-6">
                        <button
                            onClick={() => handleTabSwitch('login')}
                            className={`flex-1 py-3 text-sm font-bold rounded-xl transition-all ${activeTab === 'login' ? 'bg-red-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                        >
                            Se Connecter
                        </button>
                        <button
                            onClick={() => handleTabSwitch('register')}
                            className={`flex-1 py-3 text-sm font-bold rounded-xl transition-all ${activeTab === 'register' ? 'bg-red-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                        >
                            S'inscrire
                        </button>
                    </div>
                )}

                {/* Form Container */}
                <div className={`p-8 rounded-[2.5rem] shadow-xl shadow-gray-200/50 mb-10 animate-fade-in-up bg-white border border-gray-100`}>
                    <form onSubmit={activeTab === 'login' ? handleLogin : handleRegister} className="space-y-5 pb-6">

                        {activeTab === 'register' && (
                            <div className="animate-fade-in">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-1 block">Nom et prénom</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => { setName(e.target.value); setError(''); }}
                                    placeholder="Ex: Amine Alje"
                                    required
                                    className={`w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:bg-white transition-all text-gray-900 font-medium ${activeRole === 'livreur' ? 'focus:ring-blue-500' : 'focus:ring-red-600'}`}
                                />
                            </div>
                        )}

                        {activeTab === 'register' && activeRole === 'restaurateur' && (
                            <div className="animate-fade-in">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-1 block">Nom du Restaurant</label>
                                <input
                                    type="text"
                                    value={restaurantName}
                                    onChange={(e) => { setRestaurantName(e.target.value); setError(''); }}
                                    placeholder="Ex: Le Gourmet"
                                    required
                                    className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-600 focus:bg-white transition-all text-gray-900 font-medium"
                                />
                            </div>
                        )}

                        <div>
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-1 block">Email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                                placeholder="contact@pro.com"
                                required
                                className={`w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:bg-white transition-all text-gray-900 font-medium ${activeRole === 'livreur' ? 'focus:ring-blue-500' : 'focus:ring-red-600'}`}
                            />
                        </div>

                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 block">Mot de passe</label>
                                {activeTab === 'login' && (
                                    <button type="button" onClick={onForgotPassword} className="text-xs text-gray-500 font-bold hover:underline">Oublié ?</button>
                                )}
                            </div>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                                placeholder="••••••••"
                                required
                                className={`w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:bg-white transition-all text-gray-900 font-medium ${activeRole === 'livreur' ? 'focus:ring-blue-500' : 'focus:ring-red-600'}`}
                            />
                        </div>

                        {/* Extra fields for Restaurateur & Livreur Register */}
                        {activeTab === 'register' && (activeRole === 'restaurateur' || activeRole === 'livreur') && (
                            <div className="animate-fade-in space-y-5">
                                <div>
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-1 block">Wilaya</label>
                                    <select
                                        value={wilaya}
                                        onChange={(e) => { setWilaya(e.target.value); setError(''); }}
                                        required
                                        className={`w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:bg-white transition-all text-gray-900 font-medium appearance-none ${activeRole === 'livreur' ? 'focus:ring-blue-500' : 'focus:ring-red-600'}`}
                                    >
                                        <option value="" disabled>Sélectionnez votre Wilaya</option>
                                        {wilayas.map((w: any) => (
                                            <option key={w} value={w}>{w}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-1 block">Num tel</label>
                                    <input
                                        type="tel"
                                        value={phone}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                            setPhone(val);
                                            setError('');
                                        }}
                                        maxLength={10}
                                        placeholder="05 / 06 / 07 ..."
                                        required
                                        className={`w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:bg-white transition-all text-gray-900 font-medium ${activeRole === 'livreur' ? 'focus:ring-blue-500' : 'focus:ring-red-600'}`}
                                    />
                                </div>
                            </div>
                        )}

                        {cooldown > 0 && activeTab === 'register' && (
                            <div className="p-3 bg-yellow-50 border border-yellow-100 rounded-xl text-sm text-yellow-700 text-center font-medium">
                                Veuillez patienter {cooldown}s.
                            </div>
                        )}

                        <div className="pt-2">
                            <button
                                type="submit"
                                disabled={isLoading || (activeTab === 'register' && cooldown > 0)}
                                className={`w-full py-5 font-extrabold text-white rounded-[1.5rem] transition-all shadow-xl disabled:bg-gray-200 disabled:shadow-none active:scale-[0.98] text-lg ${activeRole === 'livreur' ? 'bg-blue-600 hover:bg-blue-700 shadow-blue-100' : 'bg-red-600 hover:bg-red-700 shadow-red-200'}`}
                            >
                                {isLoading ? (
                                    <div className="flex items-center justify-center gap-2">
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        <span>Patientez...</span>
                                    </div>
                                ) : (activeTab === 'login' ? (isSuperAdminPortal ? 'Connexion Admin' : 'Se Connecter') : "Créer mon Compte")}
                            </button>
                        </div>
                    </form>

                    <div className="relative flex items-center py-6">
                        <div className="flex-grow border-t border-gray-200"></div>
                        <span className="flex-shrink mx-4 text-gray-400 text-sm font-medium">OU</span>
                        <div className="flex-grow border-t border-gray-200"></div>
                    </div>

                    <button
                        onClick={handleGoogleAuth}
                        disabled={isLoading}
                        className="w-full py-4 flex items-center justify-center gap-3 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all disabled:bg-gray-100 disabled:opacity-50 active:scale-[0.98]"
                    >
                        <GoogleIcon className="w-6 h-6" />
                        <span className="font-bold text-gray-700">Continuer avec Google</span>
                    </button>
                </div>
            </div>

            {/* --- Error / Success Modals --- */}
            {successMessage && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white w-full max-w-sm rounded-3xl shadow-2xl overflow-hidden animate-pop-in p-8 text-center">
                        <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                            <span className="text-3xl">📧</span>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Vérifiez vos emails</h2>
                        <p className="text-gray-600 mb-8 font-medium italic">
                            "Une petite validation par mail est requise, consultez votre boite mail et n'hésitez pas à voir dans les Spams."
                        </p>
                        <button
                            onClick={() => { setSuccessMessage(''); setActiveTab('login'); }}
                            className="w-full py-4 bg-gray-900 hover:bg-black text-white font-extrabold rounded-2xl shadow-lg transition-all active:scale-[0.98] text-lg"
                        >
                            J'ai compris
                        </button>
                    </div>
                </div>
            )}

            {showErrorModal && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
                    <div className="bg-white rounded-3xl shadow-2xl p-6 w-full max-w-sm text-center border border-gray-100">
                        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
                            <span className="text-3xl">⚠️</span>
                        </div>
                        <h3 className="text-xl font-extrabold text-gray-900 mb-2">Attention</h3>
                        <p className="text-gray-600 mb-8 px-2 font-medium leading-relaxed">{error}</p>
                        <button
                            onClick={() => setShowErrorModal(false)}
                            className="w-full py-3.5 bg-gray-900 text-white font-bold rounded-xl hover:bg-black transition-colors shadow-lg active:scale-95 duration-150"
                        >
                            Fermer
                        </button>
                    </div>
                </div>
            )}

            {showRoleMismatchModal && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-md p-4 animate-fade-in">
                    <div className="bg-white rounded-[2.5rem] shadow-2xl p-8 w-full max-w-sm text-center border border-white/20 animate-pop-in">
                        <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-blue-50 mb-6">
                            <span className="text-4xl">😋</span>
                        </div>
                        <h3 className="text-2xl font-black text-gray-900 mb-3 tracking-tight">Profil Client détecté</h3>
                        <p className="text-gray-600 mb-8 px-2 font-medium leading-relaxed">
                            {error}
                        </p>
                        <div className="space-y-3">
                            <button
                                onClick={() => {
                                    setShowRoleMismatchModal(false);
                                    onSwitchToClient();
                                }}
                                className="w-full py-4 bg-red-600 text-white font-black rounded-2xl hover:bg-red-700 transition-all shadow-xl shadow-red-200 active:scale-95"
                            >
                                Aller à l'Espace Client
                            </button>
                            <button
                                onClick={() => setShowRoleMismatchModal(false)}
                                className="w-full py-3 text-gray-400 font-bold hover:text-gray-600 transition-colors"
                            >
                                Annuler
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showIncorrectPasswordPopup && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl animate-scale-up">
                        <div className="p-6 text-center">
                            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                                <span className="text-3xl">⚠️</span>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Mot de passe incorrect</h3>
                            <p className="text-sm text-gray-600 mb-6">Le mot de passe saisi pour <strong>{email}</strong> est incorrect.</p>
                            <div className="space-y-3">
                                <button onClick={() => setShowIncorrectPasswordPopup(false)} className="w-full py-3 font-bold text-white bg-gray-900 rounded-xl hover:bg-black transition-colors shadow-sm">Réessayer</button>
                                <button
                                    onClick={() => { setShowIncorrectPasswordPopup(false); onForgotPassword(); }}
                                    className="w-full py-3 font-bold text-gray-700 border-2 border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                                >Mot de passe oublié ?</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showExistingUserPopup && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl animate-scale-up">
                        <div className="p-8 text-center">
                            <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                                <span className="text-2xl">👋</span>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Vous êtes déjà membre</h3>
                            <p className="text-sm text-gray-600 mb-8">Un compte pour <strong>{email}</strong> existe déjà sur Yumii.</p>
                            <div className="space-y-3">
                                <button onClick={() => { setShowExistingUserPopup(false); setActiveTab('login'); }} className="w-full py-4 font-bold text-white bg-gray-900 rounded-xl hover:bg-black transition-colors">Me connecter</button>
                                <button onClick={() => setShowExistingUserPopup(false)} className="w-full py-3 text-sm font-bold text-gray-500 hover:text-gray-800 transition-colors">Annuler</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default ProAuthScreen;
